import { camelize, snakeize } from 'casing'
import { isEmpty, isEqual } from 'lodash'
import { DateTime } from 'luxon'
import { v4 as uuidv4 } from 'uuid'
import { toIsoDateTimeString } from '../../utils/isoDateTimeUtils'
import type { UserProfileState } from '../../modules/userProfile/reducer'
import type { LockMetadata } from '../../types/flyoutOnboarding'
import flyoutOnboardingFormData from './flyoutOnboardingMockData.json'

const autoSaveThresoldTime =
  Number(import.meta.env.VITE_AUTOSAVE_THRESOLD_TIME) || 120

const LABRA_PARTNER_DETAILS = 'labraPartnerDetails'
const CLOUD_SETTINGS = 'cloudSettings'
const PAGE_MARKER = 'pageMarker'

const TECHNOLOGY_PARTNER = 'TECHNOLOGY_PARTNER'
const CONSULTING_PARTNER = 'CONSULTING_PARTNER'

export type OnboardingLockMetadata = LockMetadata

export const shouldAcquireOnboardingLock = (lastActivityAt?: string) => {
  const timeDiff = Math.ceil(
    DateTime.now().diff(
      lastActivityAt ? DateTime.fromISO(lastActivityAt) : DateTime.now(),
      ['seconds']
    ).seconds
  )
  return timeDiff > autoSaveThresoldTime || isEmpty(lastActivityAt)
}

export const buildLockMetadataForCurrentUser = (
  lockMetadata: LockMetadata | undefined,
  user: UserProfileState
): LockMetadata => {
  if (!window.name) {
    window.name = uuidv4()
  }
  return {
    ...lockMetadata,
    windowId: window.name,
    lockedBy: user.userProfile.id,
    lockedByName:
      `${user.userProfile.firstName || ''} ${user.userProfile.lastName || ''}`.trim(),
    lockByEmail: user.userProfile.email,
    lockAcquisitionProbe: true,
  }
}

export const applyDefaultCloudSettings = (
  initialFlyoutOnboardingFormData: Record<string, any>,
  partnerId: string
) => {
  const cloudSettings = initialFlyoutOnboardingFormData.cloud_settings || {}
  if (cloudSettings.is_own_s3_bucket === undefined) {
    cloudSettings.is_own_s3_bucket = false
  }
  if (cloudSettings.stack_type === undefined) {
    cloudSettings.stack_type = 'cloudformation'
  }
  if (cloudSettings.cloud_external_id === undefined) {
    cloudSettings.cloud_external_id = partnerId
  }
  initialFlyoutOnboardingFormData.cloud_settings = cloudSettings
  return initialFlyoutOnboardingFormData
}

export const setInitialPartnerData = (partnerData: {
  partnershipType?: string | string[]
}) => {
  const initialFlyoutOnboardingFormData = JSON.parse(
    JSON.stringify(flyoutOnboardingFormData)
  )
  const consultingPartnerISVLabraSharingTerm = {
    label:
      'I agree to provide my ISV partners on Labra with offer details linked to their products',
    value: false,
    type: 'checkbox',
  }
  const partnershipType = partnerData.partnershipType
  if (partnershipType) {
    const types = Array.isArray(partnershipType)
      ? partnershipType
      : [partnershipType]
    if (
      types.includes(TECHNOLOGY_PARTNER) &&
      types.includes(CONSULTING_PARTNER)
    ) {
      initialFlyoutOnboardingFormData.labra_partner_details.partnership_type = [
        'ISV',
        'CP',
      ]
      initialFlyoutOnboardingFormData.terms_and_conditions.labra_partner_details.push(
        consultingPartnerISVLabraSharingTerm
      )
    } else if (types.includes(TECHNOLOGY_PARTNER)) {
      initialFlyoutOnboardingFormData.labra_partner_details.partnership_type = [
        'ISV',
      ]
    } else {
      initialFlyoutOnboardingFormData.labra_partner_details.partnership_type = [
        'CP',
      ]
      initialFlyoutOnboardingFormData.terms_and_conditions.labra_partner_details.push(
        consultingPartnerISVLabraSharingTerm
      )
    }
  }
  return initialFlyoutOnboardingFormData
}

export const mapCloudCurrenciesToCurrencies = (cloudSettings?: {
  cloudCurrencies?: unknown
  currencies?: unknown
  [key: string]: unknown
}) => {
  if (cloudSettings?.cloudCurrencies !== undefined) {
    cloudSettings.currencies = cloudSettings.cloudCurrencies
    delete cloudSettings.cloudCurrencies
  }
}

export const normalizeOnboardingApiDetails = (details: Record<string, any>) => {
  if (details.cloudSettings?.cloudCurrencies !== undefined) {
    mapCloudCurrenciesToCurrencies(details.cloudSettings)
  }

  if (details.cloudSettings && details.cloudSettings.stackType === undefined) {
    details.cloudSettings.stackType = 'cloudformation'
  }
  if (
    details.cloudSettings?.stackType === 'terraform' &&
    details.cloudSettings.terraformConnectionValidated === undefined
  ) {
    details.cloudSettings.terraformConnectionValidated = false
  }

  if (details.labraPartnerDetails?.partnershipType) {
    const partnershipType = details.labraPartnerDetails.partnershipType
    if (!Array.isArray(partnershipType)) {
      details.labraPartnerDetails.partnershipType = [partnershipType]
    }
  }

  return details
}

const checkAllCompleted = ({
  pageMarker,
  page,
  isManaged,
}: {
  pageMarker: { page1?: boolean; page2?: boolean }
  page: string
  isManaged?: boolean
}) => {
  if (page === LABRA_PARTNER_DETAILS) {
    return isManaged || pageMarker.page2
  } else if (page === CLOUD_SETTINGS) {
    return pageMarker.page1
  }
}

export const isChangeInOnboardingData = (
  onboardingData: any,
  getOnboardingStateData: any
) => {
  const { lockMetadata: _metaData, ...restOnboardingData } = onboardingData
  const { lockMetadata: _lockMetadata, ...restGetOnboardingStateData } =
    getOnboardingStateData || {}
  return !isEqual(restOnboardingData, restGetOnboardingStateData)
}

export const processFlyoutOnboarding = ({
  isAutoSave,
  onboardingForm,
  pageMarkerMapping,
  page,
  status,
  isFormUpdated,
  partnerId,
}: {
  isAutoSave: boolean
  onboardingForm: any
  pageMarkerMapping: Record<string, string>
  page: string
  status?: string
  isFormUpdated: boolean
  partnerId: string
}) => {
  const { cloudSettings, ...restOnboardingForm } = onboardingForm
  const { currencies, ...restCloudSettings } = cloudSettings || {}

  const cloudSettingsWithAutomationRule = restCloudSettings
    ? {
        ...restCloudSettings,
        automation: !(restCloudSettings.isOwnS3Bucket ?? false),
      }
    : restCloudSettings

  const cleanedCloudSettings = cloudSettingsWithAutomationRule
    ? Object.fromEntries(
        Object.entries(cloudSettingsWithAutomationRule).filter(
          ([key]) => key !== 'sellerType'
        )
      )
    : cloudSettingsWithAutomationRule

  const cloudSettingsForSave = cleanedCloudSettings
    ? {
        ...cleanedCloudSettings,
        cloudExternalId: String(partnerId).trim(),
      }
    : cleanedCloudSettings

  const snakeizedData = snakeize({
    ...restOnboardingForm,
    cloudSettings: cloudSettingsForSave,
    lockMetadata: {
      ...onboardingForm.lockMetadata,
      ...(isFormUpdated || status === 'lockAcquire'
        ? { lastActivityAt: DateTime.now().toUTC().toISO() }
        : {}),
      editingEnabled: true,
    },
    ...(!isAutoSave && {
      pageMarker: {
        ...onboardingForm.pageMarker,
        [camelize(pageMarkerMapping[page])]: true,
      },
      completed: {
        value: checkAllCompleted({
          pageMarker: onboardingForm[PAGE_MARKER],
          page,
          isManaged: onboardingForm[LABRA_PARTNER_DETAILS]?.isManaged,
        }),
      },
    }),
  })

  if (currencies && snakeizedData.cloud_settings) {
    snakeizedData.cloud_settings.cloud_currencies = currencies
  }

  return snakeizedData
}

const prerequisitesFromArray = (
  prerequisitesArray: { id: string; value: boolean }[] = []
) => {
  const prerequisitesMap = prerequisitesArray.reduce(
    (acc, item) => {
      acc[item.id] = item.value
      return acc
    },
    {} as Record<string, boolean>
  )

  return {
    hasAccessedMpc: prerequisitesMap.hasAccessedMpc || false,
    hasEnrolledInCommercialMarketplace:
      prerequisitesMap.hasEnrolledInCommercialMarketplace || false,
    hasCompletedOrganizationProfile:
      prerequisitesMap.hasCompletedOrganizationProfile || false,
  }
}

const prerequisitesToArray = (prerequisites: {
  hasAccessedMpc?: boolean
  hasEnrolledInCommercialMarketplace?: boolean
  hasCompletedOrganizationProfile?: boolean
} = {}) => {
  return [
    { id: 'hasAccessedMpc', value: prerequisites.hasAccessedMpc },
    {
      id: 'hasEnrolledInCommercialMarketplace',
      value: prerequisites.hasEnrolledInCommercialMarketplace,
    },
    {
      id: 'hasCompletedOrganizationProfile',
      value: prerequisites.hasCompletedOrganizationProfile,
    },
  ]
}

export const convertApiResponseToReducer = (apiResponse: any) => {
  const draftJson = apiResponse?.draft_json || {}
  const cloudSettings = draftJson.cloud_settings || {}
  const prerequisites = cloudSettings.cloud_prerequisites || []
  const accountConfiguration = cloudSettings.account_configuration || {}
  const cloudCredentials = cloudSettings.cloud_credentials || {}
  const lockMetadata = apiResponse?.lock_metadata || {}
  const companyInfo = draftJson?.labra_partner_details || {}
  const prerequisitesObj = prerequisitesFromArray(prerequisites)

  return {
    hasAccessedMpc: prerequisitesObj.hasAccessedMpc,
    hasEnrolledInCommercialMarketplace:
      prerequisitesObj.hasEnrolledInCommercialMarketplace,
    hasCompletedOrganizationProfile:
      prerequisitesObj.hasCompletedOrganizationProfile,
    sellerType: Array.isArray(accountConfiguration.seller_type)
      ? accountConfiguration.seller_type
      : accountConfiguration.seller_type
        ? [accountConfiguration.seller_type]
        : [],
    currencies:
      accountConfiguration.currencies?.length > 0
        ? accountConfiguration.currencies
        : ['USD'],
    connectionName: cloudCredentials.connection_name || '',
    tenantId: cloudCredentials.tenant_id || '',
    clientId: cloudCredentials.client_id || '',
    clientSecret: cloudCredentials.client_secret || '',
    clientSecretExpirationDate: toIsoDateTimeString(
      cloudCredentials.client_secret_expiration_date || ''
    ),
    mpnId: cloudCredentials.mpn_id || '',
    connectionTested: 'standBy',
    lockMetadata: {
      lockedBy: lockMetadata?.locked_by || '',
      lockedByName: lockMetadata?.locked_by_name || '',
      lockByEmail: lockMetadata?.lock_by_email || '',
      windowId: lockMetadata?.window_id || '',
      lastActivityAt: lockMetadata?.last_activity_at || '',
      lockAcquisitionProbe: lockMetadata?.lock_acquisition_probe || false,
      editingEnabled: lockMetadata?.editing_enabled || false,
    } as LockMetadata,
    filledCloudSettings: cloudSettings.form_filled || false,
    filledCompanyInfo: companyInfo?.form_filled || false,
    companyName: companyInfo?.company_name || '',
    companyIndustry: companyInfo?.industry || '',
    companyWebsite: companyInfo?.company_website || '',
    acceptedTermsAndConditions: companyInfo?.accepted_terms || false,
    submit: apiResponse?.submit || false,
  }
}

export const convertMicrosoftFormToApiPayload = (
  partnerId: string,
  microsoftOnboardingForm: any
) => {
  return {
    partner_id: partnerId,
    draft_json: {
      labra_partner_details: {
        form_filled: microsoftOnboardingForm.filledCompanyInfo,
        company_name: microsoftOnboardingForm.companyInfo?.companyName || '',
        company_website:
          microsoftOnboardingForm.companyInfo?.companyWebsite || '',
        industry: microsoftOnboardingForm.companyInfo?.companyIndustry || '',
        accepted_terms:
          microsoftOnboardingForm.companyInfo?.acceptedTermsAndConditions ||
          false,
      },
      cloud_settings: {
        form_filled: microsoftOnboardingForm.filledCloudSettings,
        cloud_prerequisites: prerequisitesToArray(
          microsoftOnboardingForm.prerequisites
        ),
        account_configuration: {
          seller_type:
            microsoftOnboardingForm.accountConfiguration?.sellerType || [],
          currencies: microsoftOnboardingForm.accountConfiguration
            ?.currencies || ['USD'],
        },
        cloud_credentials: {
          connection_name:
            microsoftOnboardingForm.cloudCredentials?.connectionName || '',
          tenant_id: microsoftOnboardingForm.cloudCredentials?.tenantId || '',
          client_id: microsoftOnboardingForm.cloudCredentials?.clientId || '',
          client_secret:
            microsoftOnboardingForm.cloudCredentials?.clientSecret || '',
          client_secret_expiration_date: toIsoDateTimeString(
            microsoftOnboardingForm.cloudCredentials
              ?.clientSecretExpirationDate || ''
          ),
          mpn_id: microsoftOnboardingForm.cloudCredentials?.mpnId || '',
        },
      },
    },
    lock_metadata: {
      locked_by: microsoftOnboardingForm.lockMetadata?.lockedBy,
      last_activity_at: microsoftOnboardingForm.lockMetadata?.lastActivityAt,
      lock_acquisition_probe:
        microsoftOnboardingForm.lockMetadata?.lockAcquisitionProbe,
      window_id: microsoftOnboardingForm.lockMetadata?.windowId,
      locked_by_name: microsoftOnboardingForm.lockMetadata?.lockedByName,
      lock_by_email: microsoftOnboardingForm.lockMetadata?.lockByEmail,
      editing_enabled: microsoftOnboardingForm.lockMetadata?.editingEnabled,
    },
    submit: microsoftOnboardingForm.submit,
  }
}

export const convertReducerToMicrosoftForm = (reducerData: any) => {
  return {
    prerequisites: {
      hasAccessedMpc: reducerData.hasAccessedMpc,
      hasEnrolledInCommercialMarketplace:
        reducerData.hasEnrolledInCommercialMarketplace,
      hasCompletedOrganizationProfile:
        reducerData.hasCompletedOrganizationProfile,
    },
    accountConfiguration: {
      sellerType: reducerData.sellerType,
      currencies: reducerData.currencies,
    },
    cloudCredentials: {
      connectionName: reducerData.connectionName,
      tenantId: reducerData.tenantId,
      clientId: reducerData.clientId,
      clientSecret: reducerData.clientSecret,
      clientSecretExpirationDate: reducerData.clientSecretExpirationDate,
      mpnId: reducerData.mpnId,
      connectionTested: reducerData.connectionTested,
    },
    steps: {
      prerequisites: reducerData.hasAccessedMpc,
      accountConfiguration: reducerData.hasCompletedOrganizationProfile,
      cloudCredentials: reducerData.connectionTested === 'success',
    },
    lockMetadata: {
      lockedBy: reducerData.lockMetadata?.lockedBy,
      lockedByName: reducerData.lockMetadata?.lockedByName,
      lockByEmail: reducerData.lockMetadata?.lockByEmail,
      windowId: reducerData.lockMetadata?.windowId,
      lastActivityAt: reducerData.lockMetadata?.lastActivityAt,
      lockAcquisitionProbe: reducerData.lockMetadata?.lockAcquisitionProbe,
      editingEnabled: reducerData.lockMetadata?.editingEnabled,
    },
    companyInfo: {
      companyName: reducerData.companyName,
      companyIndustry: reducerData.companyIndustry,
      companyWebsite: reducerData.companyWebsite,
      acceptedTermsAndConditions: reducerData.acceptedTermsAndConditions,
    },
    filledCloudSettings: reducerData.filledCloudSettings,
    filledCompanyInfo: reducerData.filledCompanyInfo,
    submit: reducerData.submit,
  }
}
